import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import useResolvedTimezone from "@/hooks/useResolvedTimezone";
import { formatDateTimeInSiteTz } from "@/utils/formatInSiteTz";
import { SecondaryButton } from "@/components/ds";

// ── Types ────────────────────────────────────────────────────────────────────

interface LogEntry {
  id: number;
  integration: string;
  severity: "Info" | "Warning" | "Error";
  category: string;
  message: string;
  additionalInfo?: string;
  actorUserId?: string;
  actorEmail?: string;
  suppressedCount: number;
  createdAt: number; // Unix ms
}

interface SkillEntry {
  id: number;
  conversationId: number;
  toolName: string;
  category: string;
  resultPreview?: string;
  success: boolean;
  durationMs: number;
  calledAt: number; // Unix ms
}

interface LogPage<T> {
  total: number;
  page: number;
  pageSize: number;
  items: T[];
}

interface Props {
  /** If provided, scopes to this integration slug via GET /Integration/Logs/{slug} */
  integrationSlug?: string;
  /** Hide the "AI Skill Calls" tab — for non-integration audit trails (e.g. Team/Workflow config-change logs) where skill calls are meaningless. */
  hideSkillsTab?: boolean;
}

// ── Badge helpers ─────────────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<string, string> = {
  Info: "bg-zinc-100 text-zinc-600",
  Warning: "bg-amber-100 text-amber-700",
  Error: "bg-red-100 text-red-700"
};

const CATEGORY_STYLES: Record<string, string> = {
  Auth: "bg-purple-50 text-purple-600",
  Security: "bg-orange-50 text-orange-700",
  Config: "bg-primary-bg text-primary-active",
  Request: "bg-zinc-50 text-zinc-500",
  Response: "bg-zinc-50 text-zinc-500",
  AiTool: "bg-green-50 text-green-700",
  Setup: "bg-teal-50 text-teal-700",
  Error: "bg-red-50 text-red-500"
};

function SeverityBadge({ severity }: { severity: string }) {
  const cls = SEVERITY_STYLES[severity] ?? "bg-zinc-100 text-zinc-500";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {severity}
    </span>
  );
}

function CategoryBadge({ category }: { category: string }) {
  const cls = CATEGORY_STYLES[category] ?? "bg-zinc-50 text-zinc-500";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}
    >
      {category}
    </span>
  );
}

function SuccessBadge({ success }: { success: boolean }) {
  return success ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-700">
      OK
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700">
      Error
    </span>
  );
}

function formatTs(ms: number, tz?: string) {
  return formatDateTimeInSiteTz(ms, tz, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).replace(",", "");
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SEVERITY_OPTIONS = ["All", "Info", "Warning", "Error"];
const CATEGORY_OPTIONS = [
  "All",
  "Auth",
  "Config",
  "Request",
  "Response",
  "Security",
  "AiTool",
  "Setup",
  "Error"
];
const PAGE_SIZES = [25, 50, 100];

const INTEGRATIONS = [
  "All",
  // CRM
  "NetSuite",
  "HubSpot",
  "Salesforce",
  "Dynamics365",
  "Pipedrive",
  "Zoho",
  "NativeCrm",
  "Zendesk",
  // E-commerce
  "Shopify",
  "BigCommerce",
  "WooCommerce",
  "Squarespace",
  "Magento",
  "Klaviyo",
  // Government / Vertical
  "Accela",
  "Formstack",
  // Hospitality
  "Uplisting",
  "Guesty",
  "Cloudbeds",
  // POS / Booking / Payments
  "Square",
  "Toast",
  "Mindbody",
  "QuickBooks",
  "Stripe",
  // Scheduling
  "OutlookCalendar",
  "Calendly",
  "MicrosoftBookings",
  "Appointments",
  // Notifications
  "TeamsNotifications",
  "SlackNotifications",
  // Email Inboxes
  "GmailInbox",
  "OutlookInbox",
  // Channels
  "WhatsApp",
  // Project Management
  "Monday",
  // Automation
  "Zapier",
  "PowerAutomate",
  "Make",
  // Platform
  "MCP",
  "Microsoft365",
  "TranscriptStorage",
  "GoogleTagManager",
  "GA4",
  // ITSM
  "ServiceNow",
  // Recruiting
  "Recruiting",
  // Call attribution
  "CallRail",
  "Webflow",
  // Texting
  "PickupNotifications"
];

type Tab = "activity" | "skills";

// ── Activity log tab ──────────────────────────────────────────────────────────

function ActivityLogTab({ integrationSlug }: { integrationSlug?: string }) {
  const api = useApi();
  const siteTimezone = useResolvedTimezone();

  const [data, setData] = useState<LogPage<LogEntry> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [severity, setSeverity] = useState("All");
  const [category, setCategory] = useState("All");
  const [integration, setIntegration] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
  }, [search]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
      });
      if (severity !== "All") params.set("severity", severity);
      if (category !== "All") params.set("category", category);
      if (!integrationSlug && integration !== "All")
        params.set("integration", integration);
      if (debouncedSearch) params.set("search", debouncedSearch);

      const url = integrationSlug
        ? `Integration/Logs/${integrationSlug}?${params}`
        : `Integration/Logs?${params}`;

      const resp = await api.messaging.get(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json: LogPage<LogEntry> = await resp.json();
      setData(json);
    } catch {
      setError("Failed to load integration logs.");
    } finally {
      setLoading(false);
    }
  }, [
    api.messaging,
    integrationSlug,
    severity,
    category,
    integration,
    debouncedSearch,
    page,
    pageSize
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  useEffect(() => {
    setPage(1);
  }, [severity, category, integration, debouncedSearch, pageSize]);

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const visibleItems = data?.items ?? [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search logs…"
          className="px-3 py-1.5 text-xs border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary w-52"
          aria-label="Search log messages"
        />
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary"
          aria-label="Filter by severity"
        >
          {SEVERITY_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === "All" ? "All severities" : s}
            </option>
          ))}
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary"
          aria-label="Filter by category"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c === "All" ? "All categories" : c}
            </option>
          ))}
        </select>
        {!integrationSlug && (
          <select
            value={integration}
            onChange={(e) => setIntegration(e.target.value)}
            className="px-2 py-1.5 text-xs border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary"
            aria-label="Filter by integration"
          >
            {INTEGRATIONS.map((i) => (
              <option key={i} value={i}>
                {i === "All" ? "All integrations" : i}
              </option>
            ))}
          </select>
        )}
        {(severity !== "All" ||
          category !== "All" ||
          integration !== "All" ||
          search) && (
          <SecondaryButton
            type="button"
            onClick={() => {
              setSeverity("All");
              setCategory("All");
              setIntegration("All");
              setSearch("");
            }}
            className="!bg-transparent hover:!bg-zinc-50 !px-2 !py-1.5 !text-xs !font-normal !text-zinc-500 hover:!text-zinc-700 border !border-zinc-200 !rounded-lg"
          >
            Clear filters
          </SecondaryButton>
        )}
        <span className="ml-auto text-xs text-zinc-400">
          {data ? `${data.total} total entries` : ""}
        </span>
        <SecondaryButton
          type="button"
          onClick={fetchLogs}
          className="!bg-transparent hover:!bg-cornflower-blue-50 !px-2 !py-1.5 !text-xs !font-normal !text-primary hover:!text-cornflower-blue-800 border !border-cornflower-blue-200 !rounded-lg"
          aria-label="Refresh logs"
        >
          Refresh
        </SecondaryButton>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-hidden">
        {loading && (
          <div className="text-center py-6 text-xs text-zinc-400">Loading…</div>
        )}
        {error && (
          <div className="text-center py-6 text-xs text-red-500">{error}</div>
        )}
        {!loading && !error && visibleItems.length === 0 && (
          <div className="text-center py-10 text-xs text-zinc-400">
            No log entries found.{" "}
            {(severity !== "All" || category !== "All" || search) &&
              "Try clearing the filters."}
          </div>
        )}
        {!loading && visibleItems.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium w-36">
                  Timestamp
                </th>
                {!integrationSlug && (
                  <th className="px-3 py-2 text-left text-zinc-500 font-medium w-24">
                    Integration
                  </th>
                )}
                <th className="px-3 py-2 text-left text-zinc-500 font-medium w-20">
                  Severity
                </th>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium w-24">
                  Category
                </th>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium w-44">
                  Who
                </th>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium">
                  Message
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {visibleItems.map((item) => {
                const expanded = expandedIds.has(item.id);
                const hasDetail =
                  item.additionalInfo && item.additionalInfo.trim().length > 0;
                return (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`${hasDetail ? "cursor-pointer hover:bg-zinc-50" : ""} transition-colors`}
                      onClick={() => hasDetail && toggleExpand(item.id)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-zinc-500 font-mono">
                        {formatTs(item.createdAt, siteTimezone)}
                      </td>
                      {!integrationSlug && (
                        <td className="px-3 py-2 whitespace-nowrap text-zinc-600 font-medium">
                          {item.integration}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <SeverityBadge severity={item.severity} />
                      </td>
                      <td className="px-3 py-2">
                        <CategoryBadge category={item.category} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-zinc-600">
                        {item.actorEmail ? (
                          <span className="font-medium">{item.actorEmail}</span>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-zinc-700 wrap-break-word">
                        <span>{item.message}</span>
                        {item.suppressedCount > 0 && (
                          <span className="ml-2 text-[10px] text-amber-600 font-medium">
                            +{item.suppressedCount} suppressed
                          </span>
                        )}
                        {hasDetail && (
                          <span className="ml-2 text-[10px] text-primary">
                            {expanded ? "▲ hide details" : "▼ details"}
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded && hasDetail && (
                      <tr className="bg-zinc-50">
                        <td
                          colSpan={integrationSlug ? 5 : 6}
                          className="px-3 py-2"
                        >
                          <pre className="text-[11px] text-zinc-600 whitespace-pre-wrap wrap-break-word font-mono bg-zinc-100 rounded p-2 max-h-40 overflow-y-auto">
                            {item.additionalInfo}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>
            Page {page} of {totalPages} &mdash; {data.total} entries
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="First page"
            >
              «
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="Previous page"
            >
              ‹
            </button>
            <span className="px-2">{page}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="Next page"
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="Last page"
            >
              »
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span>Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="text-xs border border-zinc-200 rounded px-1 py-0.5 focus:outline-none"
              aria-label="Rows per page"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Skill calls tab ───────────────────────────────────────────────────────────

function SkillCallsTab({ integrationSlug }: { integrationSlug?: string }) {
  const api = useApi();
  const siteTimezone = useResolvedTimezone();

  const [data, setData] = useState<LogPage<SkillEntry> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [errorsOnly, setErrorsOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
  }, [search]);

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
      });
      if (integrationSlug) params.set("category", integrationSlug);
      if (errorsOnly) params.set("errorsOnly", "true");
      if (debouncedSearch) params.set("search", debouncedSearch);

      const resp = await api.messaging.get(`Integration/SkillLogs?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json: LogPage<SkillEntry> = await resp.json();
      setData(json);
    } catch {
      setError("Failed to load skill call logs.");
    } finally {
      setLoading(false);
    }
  }, [
    api.messaging,
    integrationSlug,
    errorsOnly,
    debouncedSearch,
    page,
    pageSize
  ]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);
  useEffect(() => {
    setPage(1);
  }, [errorsOnly, debouncedSearch, pageSize]);

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const visibleItems = data?.items ?? [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search skills…"
          className="px-3 py-1.5 text-xs border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary w-52"
          aria-label="Search skill calls"
        />
        <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={errorsOnly}
            onChange={(e) => setErrorsOnly(e.target.checked)}
            className="rounded border-zinc-300"
          />
          Errors only
        </label>
        {(errorsOnly || search) && (
          <SecondaryButton
            type="button"
            onClick={() => {
              setErrorsOnly(false);
              setSearch("");
            }}
            className="!bg-transparent hover:!bg-zinc-50 !px-2 !py-1.5 !text-xs !font-normal !text-zinc-500 hover:!text-zinc-700 border !border-zinc-200 !rounded-lg"
          >
            Clear filters
          </SecondaryButton>
        )}
        <span className="ml-auto text-xs text-zinc-400">
          {data ? `${data.total} total calls` : ""}
        </span>
        <SecondaryButton
          type="button"
          onClick={fetchSkills}
          className="!bg-transparent hover:!bg-cornflower-blue-50 !px-2 !py-1.5 !text-xs !font-normal !text-primary hover:!text-cornflower-blue-800 border !border-cornflower-blue-200 !rounded-lg"
          aria-label="Refresh skill logs"
        >
          Refresh
        </SecondaryButton>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-hidden">
        {loading && (
          <div className="text-center py-6 text-xs text-zinc-400">Loading…</div>
        )}
        {error && (
          <div className="text-center py-6 text-xs text-red-500">{error}</div>
        )}
        {!loading && !error && visibleItems.length === 0 && (
          <div className="text-center py-10 text-xs text-zinc-400">
            No skill calls found.{" "}
            {errorsOnly && "Try unchecking 'Errors only'."}
          </div>
        )}
        {!loading && visibleItems.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium w-36">
                  Timestamp
                </th>
                {!integrationSlug && (
                  <th className="px-3 py-2 text-left text-zinc-500 font-medium w-24">
                    Integration
                  </th>
                )}
                <th className="px-3 py-2 text-left text-zinc-500 font-medium w-20">
                  Status
                </th>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium w-24">
                  Duration
                </th>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium">
                  Skill
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {visibleItems.map((item) => {
                const expanded = expandedIds.has(item.id);
                const hasDetail =
                  item.resultPreview && item.resultPreview.trim().length > 0;
                return (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`${hasDetail ? "cursor-pointer hover:bg-zinc-50" : ""} transition-colors`}
                      onClick={() => hasDetail && toggleExpand(item.id)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-zinc-500 font-mono">
                        {formatTs(item.calledAt, siteTimezone)}
                      </td>
                      {!integrationSlug && (
                        <td className="px-3 py-2 whitespace-nowrap text-zinc-600 font-medium">
                          {item.category}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <SuccessBadge success={item.success} />
                      </td>
                      <td className="px-3 py-2 text-zinc-500">
                        {item.durationMs}ms
                      </td>
                      <td className="px-3 py-2 text-zinc-700 wrap-break-word font-mono">
                        {item.toolName}
                        {hasDetail && (
                          <span className="ml-2 text-[10px] text-primary font-sans">
                            {expanded ? "▲ hide result" : "▼ result"}
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded && hasDetail && (
                      <tr className="bg-zinc-50">
                        <td
                          colSpan={integrationSlug ? 4 : 5}
                          className="px-3 py-2"
                        >
                          <pre className="text-[11px] text-zinc-600 whitespace-pre-wrap wrap-break-word font-mono bg-zinc-100 rounded p-2 max-h-40 overflow-y-auto">
                            {item.resultPreview}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>
            Page {page} of {totalPages} &mdash; {data.total} calls
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="First page"
            >
              «
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="Previous page"
            >
              ‹
            </button>
            <span className="px-2">{page}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="Next page"
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-2 py-1 border border-zinc-200 rounded disabled:opacity-40 hover:bg-zinc-100"
              aria-label="Last page"
            >
              »
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span>Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="text-xs border border-zinc-200 rounded px-1 py-0.5 focus:outline-none"
              aria-label="Rows per page"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function IntegrationActivityLog({
  integrationSlug,
  hideSkillsTab
}: Props) {
  const [tab, setTab] = useState<Tab>("activity");

  if (hideSkillsTab) {
    return (
      <div className="space-y-3 w-full">
        <ActivityLogTab integrationSlug={integrationSlug} />
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full">
      {/* Tab bar */}
      <div className="flex border-b border-zinc-200">
        <button
          type="button"
          onClick={() => setTab("activity")}
          className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
            tab === "activity"
              ? "border-primary text-primary-active"
              : "border-transparent text-zinc-500 hover:text-zinc-700"
          }`}
        >
          Activity Log
        </button>
        <button
          type="button"
          onClick={() => setTab("skills")}
          className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
            tab === "skills"
              ? "border-primary text-primary-active"
              : "border-transparent text-zinc-500 hover:text-zinc-700"
          }`}
        >
          AI Skill Calls
        </button>
      </div>

      {tab === "activity" && (
        <ActivityLogTab integrationSlug={integrationSlug} />
      )}
      {tab === "skills" && <SkillCallsTab integrationSlug={integrationSlug} />}
    </div>
  );
}
